"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { X, Minus, RotateCcw } from "lucide-react";
import { strings } from "@/lib/strings";
import { useTenant } from "@/lib/tenant";
import { useSalesChat } from "@/hooks/useSalesChat";
import { useQuickFlowContext } from "@/hooks/useQuickFlowContext";
import { getLatestAssistantSignals, hasUserTurn } from "@/lib/chat/messages";
import { deriveFlowProgress } from "@/lib/chat/flow-progress";
import { isSellerRefreshPage, SELLER_REFRESH_CHAT_ACTIONS, SELLER_REFRESH_CHAT_GREETING } from "@/lib/chat/seller-refresh-context";
import { ChatMessageList } from "@/components/chat/ChatMessageList";
import { ChatInput } from "@/components/chat/ChatInput";
import { ChatQuickActions } from "@/components/chat/ChatQuickActions";
import { ChatQuickReplyBar } from "@/components/chat/ChatQuickReplyBar";
import { ChatFlowFooter } from "@/components/chat/ChatFlowFooter";
interface ChatPanelProps {
  authReady: boolean;
  onClose: () => void;
  onMinimize: () => void;
  /** Race fallback: shown when Cloudflare's WAF returns `cf-mitigated: challenge`. */
  requestInteractiveToken: () => Promise<string>;
  /** PostHog distinct_id from the browser (for server-side event attribution) */
  posthogDistinctId?: string;
}

/**
 * The main chat panel showing messages, input, and quick actions.
 * Desktop: 400×600px floating panel.
 * Mobile: full-screen overlay.
 */
export function ChatPanel(t0) {
  const $ = _c(71);
  const {
    authReady,
    onClose,
    onMinimize,
    requestInteractiveToken,
    posthogDistinctId
  } = t0;
  const tenant = useTenant();
  const quickFlowContext = useQuickFlowContext();
  const t1 = usePathname();
  let t2;
  if ($[0] !== t1) {
    t2 = isSellerRefreshPage(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const sellerRefresh = t2;
  let t3;
  if ($[2] !== posthogDistinctId || $[3] !== quickFlowContext || $[4] !== requestInteractiveToken) {
    t3 = {
      quickFlowContext,
      requestInteractiveToken,
      posthogDistinctId
    };
    $[2] = posthogDistinctId;
    $[3] = quickFlowContext;
    $[4] = requestInteractiveToken;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const chat = useSalesChat(t3);
  const hasMessages = chat.messages.length > 0;
  let t4;
  if ($[6] !== authReady || $[7] !== chat) {
    t4 = (message, action) => {
      if (!authReady) {
        return;
      }
      chat.trackQuickAction(action);
      chat.sendMessage(message);
    };
    $[6] = authReady;
    $[7] = chat;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleQuickAction = t4;
  let t5;
  if ($[9] !== chat.messages) {
    t5 = getLatestAssistantSignals(chat.messages);
    $[9] = chat.messages;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const {
    quickReplies,
    inputPlaceholder
  } = t5;
  let t6;
  if ($[11] !== chat.messages || $[12] !== chat.verifiedEntityId) {
    t6 = deriveFlowProgress(chat.messages, chat.verifiedEntityId);
    $[11] = chat.messages;
    $[12] = chat.verifiedEntityId;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const flowProgress = t6;
  let t7;
  if ($[14] !== authReady || $[15] !== chat.messages || $[16] !== chat.sendMessage || $[17] !== handleQuickAction || $[18] !== quickFlowContext || $[19] !== quickReplies || $[20] !== sellerRefresh) {
    t7 = !authReady ? null : !hasUserTurn(chat.messages) ? <ChatQuickActions onSelect={handleQuickAction} quickFlowContext={quickFlowContext} actions={sellerRefresh ? SELLER_REFRESH_CHAT_ACTIONS : undefined} /> : quickReplies && quickReplies.length > 0 ? <ChatQuickReplyBar replies={quickReplies} onSelect={chat.sendMessage} /> : null;
    $[14] = authReady;
    $[15] = chat.messages;
    $[16] = chat.sendMessage;
    $[17] = handleQuickAction;
    $[18] = quickFlowContext;
    $[19] = quickReplies;
    $[20] = sellerRefresh;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  const bottomSlot = t7;
  let t8;
  if ($[22] !== tenant.name) {
    t8 = <span className="text-sm font-heading font-extrabold tracking-tight text-white">{tenant.name}</span>;
    $[22] = tenant.name;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== chat.resetChat || $[25] !== hasMessages) {
    t9 = hasMessages && <button onClick={chat.resetChat} className="rounded-lg p-1.5 text-white/50 hover:text-white hover:bg-white/10 transition-colors" aria-label={strings.chat?.reset ?? "Neuen Chat starten"} title={strings.chat?.reset ?? "Neuen Chat starten"}><RotateCcw className="h-3.5 w-3.5" /></button>;
    $[24] = chat.resetChat;
    $[25] = hasMessages;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Minus className="h-4 w-4" />;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== onMinimize) {
    t11 = <button onClick={onMinimize} className="rounded-lg p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition-colors" aria-label={strings.chat?.minimize ?? "Minimieren"}>{t10}</button>;
    $[28] = onMinimize;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <X className="h-4 w-4" />;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== onClose) {
    t13 = <button onClick={onClose} className="rounded-lg p-1.5 text-white/70 hover:text-white hover:bg-white/10 transition-colors" aria-label={strings.common.close}>{t12}</button>;
    $[31] = onClose;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== t11 || $[34] !== t13 || $[35] !== t9) {
    t14 = <div className="flex items-center gap-1">{t9}{t11}{t13}</div>;
    $[33] = t11;
    $[34] = t13;
    $[35] = t9;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t14 || $[38] !== t8) {
    t15 = <div className="shrink-0 flex items-center justify-between px-4 py-3 bg-primary-container rounded-t-none sm:rounded-t-2xl">{t8}{t14}</div>;
    $[37] = t14;
    $[38] = t8;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  let t16;
  if ($[40] !== quickFlowContext || $[41] !== sellerRefresh) {
    t16 = (quickFlowContext || sellerRefresh) && <div className="shrink-0 px-4 pt-2 pb-3"><div className="inline-flex items-center gap-1.5 rounded-full bg-accent/10 px-3 py-1 text-xs font-medium text-secondary"><span className="h-1.5 w-1.5 rounded-[9999px] bg-accent" />{sellerRefresh ? "Angebote aktualisieren" : quickFlowContext?.stepLabel}</div></div>;
    $[40] = quickFlowContext;
    $[41] = sellerRefresh;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== hasMessages || $[44] !== sellerRefresh) {
    t17 = !hasMessages && <div className="shrink-0 px-6 pt-8 pb-2 flex flex-col items-center text-center space-y-4"><div className="shrink-0"><div className="h-20 w-20 rounded-[9999px] overflow-hidden bg-primary-container p-1 ring-4 ring-secondary-fixed/20"><Image src="/images/chat/klara.jpeg" alt="Klara" width={80} height={80} className="h-full w-full rounded-[9999px] object-cover" /></div></div><h3 className="text-2xl font-extrabold text-primary">Hallo, ich bin Klara.</h3><p className="text-sm text-on-surface-variant max-w-[280px]">{sellerRefresh ? SELLER_REFRESH_CHAT_GREETING : strings.chat?.greeting ?? "Wie kann ich Sie heute bei Ihrer Kompensation unterst\xFCtzen?"}</p></div>;
    $[43] = hasMessages;
    $[44] = sellerRefresh;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== bottomSlot || $[47] !== chat.error || $[48] !== chat.isLoading || $[49] !== chat.messages || $[50] !== chat.resetChat) {
    t18 = <ChatMessageList messages={chat.messages} isLoading={chat.isLoading} error={chat.error} onReset={chat.resetChat} quickActions={bottomSlot} />;
    $[46] = bottomSlot;
    $[47] = chat.error;
    $[48] = chat.isLoading;
    $[49] = chat.messages;
    $[50] = chat.resetChat;
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  let t19;
  if ($[52] !== chat.markEntityVerified || $[53] !== flowProgress) {
    t19 = flowProgress && <ChatFlowFooter progress={flowProgress} onVerified={chat.markEntityVerified} />;
    $[52] = chat.markEntityVerified;
    $[53] = flowProgress;
    $[54] = t19;
  } else {
    t19 = $[54];
  }
  let t20;
  if ($[55] !== authReady || $[56] !== chat) {
    t20 = event => {
      event?.preventDefault();
      if (authReady) {
        chat.handleSubmit();
      }
    };
    $[55] = authReady;
    $[56] = chat;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  const t21 = chat.isLoading || !authReady;
  const t22 = inputPlaceholder ?? undefined;
  let t23;
  if ($[58] !== chat.handleInputChange || $[59] !== chat.input || $[60] !== t20 || $[61] !== t21 || $[62] !== t22) {
    t23 = <ChatInput value={chat.input} onChange={chat.handleInputChange} onSubmit={t20} isLoading={t21} placeholder={t22} />;
    $[58] = chat.handleInputChange;
    $[59] = chat.input;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
    $[63] = t23;
  } else {
    t23 = $[63];
  }
  let t24;
  if ($[64] !== t15 || $[65] !== t16 || $[66] !== t17 || $[67] !== t18 || $[68] !== t19 || $[69] !== t23) {
    t24 = <div className="flex flex-col overflow-hidden bg-surface shadow-xl fixed bottom-0 right-0 w-full h-full sm:relative sm:w-[400px] sm:h-[600px] sm:rounded-2xl sm:border sm:border-surface-container z-50">{t15}{t16}{t17}{t18}{t19}{t23}</div>;
    $[64] = t15;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
    $[68] = t19;
    $[69] = t23;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  return t24;
}